import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { AdminService } from '../../services/admin';
import { Role } from '../../../../shared/models/role';
import { RoleForm } from '../role-form/role-form';
import { RoleFormData } from '../../../../shared/models/role-form-data';
import { PermissionService } from '../../../../core/authorization/permission';
import { Observable } from 'rxjs';

@Component({
	selector: 'app-roles',
	standalone: true,
	imports: [CommonModule, MatTableModule, MatButtonModule, MatIconModule, MatSortModule, MatPaginatorModule],
	templateUrl: './roles.html',
	styleUrl: './roles.css'
})

export class Roles implements OnInit, AfterViewInit {
	dataSource       : MatTableDataSource<Role> = new MatTableDataSource<Role>([]);
	displayedColumns : string[]     = ['id', 'name', 'active', 'createdAt', 'actions'];
	canCreateRole$   : Observable<boolean>;
    canEditRole$     : Observable<boolean>;
    canDeleteRole$   : Observable<boolean>;

	@ViewChild(MatSort) sort!: MatSort;
	@ViewChild(MatPaginator) paginator!: MatPaginator;

	constructor(private adminService: AdminService, private dialog: MatDialog, private permissionService: PermissionService) {
		this.canCreateRole$ = this.permissionService.hasPermission('roles.create');
        this.canEditRole$   = this.permissionService.hasPermission('roles.update');
        this.canDeleteRole$ = this.permissionService.hasPermission('roles.delete');
	}

	ngOnInit(): void {
		this.loadRoles();
	}

	ngAfterViewInit(): void {
		this.dataSource.sort      = this.sort;
		this.dataSource.paginator = this.paginator;
	}

	async loadRoles(): Promise<void> {
		const result = await this.adminService.getAllRoles();

		if(result) {
			this.dataSource.data = result;
		}
	}

	async onCreateRole(): Promise<void> {
		const dialogRef = this.dialog.open(RoleForm, {
			width: '600px',
			disableClose: true,
			data: null
		});

		dialogRef.afterClosed().subscribe(async (result: RoleFormData | undefined) => {
			if(result) {
				const success = await this.adminService.createRole({
					id          : result.id,
					name        : result.name,
					description : result.description,
					active      : result.active,
					permissions : result.permissions
				});

				if(success) {
					this.loadRoles();
				}
			}
		});
	}

	async onEditRole(role: Role): Promise<void> {
		const formData: RoleFormData = {
			id          : role.id,
			name        : role.name,
			description : role.description,
			active      : role.active,
			permissions : role.permissions
		};

		const dialogRef = this.dialog.open(RoleForm, {
			width: '600px',
			disableClose: true,
			data: formData
		});

		dialogRef.afterClosed().subscribe(async (result: RoleFormData | undefined) => {
			if(result) {
				const roleId = result.id; 

				const updatePayload: Partial<Omit<RoleFormData, 'id'>> = {
					name        : result.name,
					description : result.description,
					active      : result.active,
					permissions : result.permissions
				};

				const success = await this.adminService.updateRole(roleId, updatePayload);

				if(success) {
					this.loadRoles();
				}
			}
		});
	}

	async onDeleteRole(id: string): Promise<void> {
		if(window.confirm('¿Estás seguro de que quieres eliminar este role? Esta acción es irreversible.')) {
			const success = await this.adminService.deleteRole(id);

			if(success) {
				this.loadRoles();
			}
		}
	}
}
